import type { ChangeEvent } from "react"
import { useEffect, useState } from "react"
import Cropper, { Area, Point } from "react-easy-crop"
import "react-easy-crop/react-easy-crop.css"
import { cn } from "../utils/cn"

type Props = {
  label: string
  description?: string
  existingUrl: string | null
  value: File | null
  onChange: (file: File | null) => void
  aspect: number
  cropShape?: "rect" | "round"
  previewClassName?: string
  previewRadius?: number
}

function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value))
}

async function cropImage(imageSrc: string, area: Area, outputName: string): Promise<File> {
  const image = new Image()
  image.src = imageSrc
  await image.decode()

  const canvas = document.createElement("canvas")
  canvas.width = Math.max(1, Math.round(area.width))
  canvas.height = Math.max(1, Math.round(area.height))

  const ctx = canvas.getContext("2d")
  if (!ctx) {
    throw new Error("Canvas context unavailable")
  }

  ctx.drawImage(image, area.x, area.y, area.width, area.height, 0, 0, canvas.width, canvas.height)

  const blob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((result) => {
      if (result) {
        resolve(result)
      } else {
        reject(new Error("Failed to crop image"))
      }
    }, "image/jpeg", 0.92)
  })

  return new File([blob], outputName.replace(/\.[^.]+$/, "") + ".jpg", { type: "image/jpeg" })
}

export function ImageCropEditor({
  label,
  description,
  existingUrl,
  value,
  onChange,
  aspect,
  cropShape = "rect",
  previewClassName = "aspect-[4/3]",
  previewRadius,
}: Props) {
  const [sourceFile, setSourceFile] = useState<File | null>(null)
  const [sourceUrl, setSourceUrl] = useState<string | null>(null)
  const [crop, setCrop] = useState<Point>({ x: 0, y: 0 })
  const [zoom, setZoom] = useState(1)
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null)
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    if (!sourceFile) {
      setSourceUrl(null)
      return
    }

    const url = URL.createObjectURL(sourceFile)
    setSourceUrl(url)
    return () => URL.revokeObjectURL(url)
  }, [sourceFile])

  const [previewUrl, setPreviewUrl] = useState<string | null>(existingUrl)

  useEffect(() => {
    if (!sourceFile) return
    setCrop({ x: 0, y: 0 })
    setZoom(1)
    setCroppedAreaPixels(null)
  }, [sourceFile])

  useEffect(() => {
    if (sourceUrl) {
      setPreviewUrl(sourceUrl)
      return
    }

    if (value) {
      const url = URL.createObjectURL(value)
      setPreviewUrl(url)
      return () => URL.revokeObjectURL(url)
    }

    setPreviewUrl(existingUrl)
  }, [existingUrl, sourceUrl, value])

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null
    if (!file) return

    setSourceFile(file)
  }

  const handleApply = async () => {
    if (!sourceFile || !sourceUrl || !croppedAreaPixels) return

    setIsSaving(true)
    try {
      const cropped = await cropImage(sourceUrl, croppedAreaPixels, sourceFile.name)
      onChange(cropped)
      setSourceFile(null)
    } finally {
      setIsSaving(false)
    }
  }

  const handleCancel = () => {
    setSourceFile(null)
  }

  return (
    <div className="space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-sm font-medium text-slate-900">{label}</div>
          {description ? <p className="mt-1 text-xs leading-5 text-slate-500">{description}</p> : null}
        </div>
        <div className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-600">
          {sourceFile ? "חיתוך" : value || existingUrl ? "מוכן" : "ריק"}
        </div>
      </div>

      <label className="block">
        <span className="label-base">בחר קובץ</span>
        <input type="file" accept="image/*" onChange={handleFileChange} className="input-base file:mr-4 file:rounded-full file:border-0 file:bg-brand-600 file:px-4 file:py-2 file:text-white" />
      </label>

      <div
        className={cn("relative overflow-hidden border border-slate-200 bg-slate-100 shadow-sm", previewClassName)}
        style={previewRadius === undefined ? undefined : { borderRadius: previewRadius === 999 ? "50%" : `${previewRadius}px` }}
      >
        {sourceFile && sourceUrl ? (
          <Cropper
            image={sourceUrl}
            crop={crop}
            zoom={zoom}
            aspect={aspect}
            cropShape={cropShape}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={(_, areaPixels) => setCroppedAreaPixels(areaPixels)}
            showGrid
          />
        ) : previewUrl ? (
          <img
            src={previewUrl}
            alt=""
            className="h-full w-full object-cover"
            style={{
              objectPosition: "center center",
              borderRadius: previewRadius === undefined ? undefined : previewRadius === 999 ? "50%" : `${previewRadius}px`,
            }}
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center p-6 text-sm text-slate-500">
            בחרו קובץ כדי לחתוך אותו
          </div>
        )}
      </div>

      {sourceFile ? (
        <div className="space-y-3 rounded-[1.75rem] border border-slate-200 bg-white p-4">
          <label className="block">
            <span className="label-base">זום</span>
            <input
              type="range"
              min="1"
              max="3"
              step="0.01"
              value={zoom}
              onChange={(event) => setZoom(clamp(Number(event.target.value), 1, 3))}
              className="mt-2 w-full"
            />
          </label>
          <div className="flex flex-wrap gap-3">
            <button type="button" className="btn-primary bg-brand-600" onClick={handleApply} disabled={isSaving}>
              {isSaving ? "שומר..." : "החל חיתוך"}
            </button>
            <button type="button" className="btn-secondary" onClick={handleCancel}>
              ביטול
            </button>
          </div>
        </div>
      ) : null}
    </div>
  )
}
